import type { Match, Player } from './types';

const POSITION_ORDER: Record<string, number> = {
  GK: 0,
  G: 0,
  BR: 0,
  LB: 10,
  LWB: 11,
  CB: 12,
  RB: 13,
  RWB: 14,
  DM: 20,
  CDM: 20,
  CM: 21,
  LM: 22,
  RM: 23,
  CAM: 24,
  AM: 24,
  LW: 30,
  RW: 31,
  CF: 32,
  ST: 33,
  FW: 33,
};

export function positionRank(position?: string | null): number {
  return POSITION_ORDER[(position ?? '').toUpperCase()] ?? 99;
}

export function playerMinutes(matches: Match[], playerId: string): number {
  return matches.reduce((total, match) => {
    const contribution = match.contributions?.find((item) => item.player_id === playerId);
    return total + Number(contribution?.minutes ?? 0);
  }, 0);
}

export function playerStats(matches: Match[], playerId: string) {
  return matches.reduce(
    (stats, match) => {
      const contribution = match.contributions?.find((item) => item.player_id === playerId);
      if (!contribution) return stats;
      stats.apps += Number(contribution.minutes ?? 0) > 0 || contribution.started ? 1 : 0;
      stats.minutes += Number(contribution.minutes ?? 0);
      stats.goals += Number(contribution.goals ?? 0);
      stats.assists += Number(contribution.assists ?? 0);
      if (Number(contribution.rating ?? 0) > 0) {
        stats.ratingTotal += Number(contribution.rating);
        stats.ratedMatches += 1;
      }
      return stats;
    },
    { apps: 0, minutes: 0, goals: 0, assists: 0, ratingTotal: 0, ratedMatches: 0 },
  );
}

export function sortPlayers(players: Player[], matches: Match[]): Player[] {
  return [...players].sort((a, b) => {
    const positionDifference = positionRank(a.position) - positionRank(b.position);
    if (positionDifference !== 0) return positionDifference;
    const minutesDifference = playerMinutes(matches, b.id) - playerMinutes(matches, a.id);
    if (minutesDifference !== 0) return minutesDifference;
    return a.name.localeCompare(b.name);
  });
}

export function attributeAverage(values?: Record<string, number> | null): number {
  const numbers = Object.values(values ?? {}).filter((value) => Number.isFinite(value));
  if (!numbers.length) return 0;
  return Math.round(numbers.reduce((sum, value) => sum + Number(value), 0) / numbers.length);
}

export function playerOverall(player: Player): number {
  const attributes = attributeAverage(player.attributes);
  const form = Math.round(Number(player.current_form ?? 6) * 10);
  const fitness = Number(player.fitness ?? 80);
  if (!attributes) return Math.round((form * 0.35 + fitness * 0.2 + Number(player.potential ?? 70) * 0.45));
  return Math.max(1, Math.min(99, Math.round(attributes * 0.7 + form * 0.18 + fitness * 0.12)));
}
